/**
 * /integrations/github — dual-purpose page
 *
 * 1. No query params, not logged in → MARKETING PAGE
 *    SEO-healthy: <title>, <meta description>, H1, CTAs (App Store, sign-up).
 *    Explains what the GitHub integration does. Indexable.
 *
 * 2. No query params, logged in → MANAGEMENT VIEW
 *    "Connect GitHub" button (or connected-status card if already installed).
 *
 * 3. With ?installation_id=N&setup_action=install (GitHub callback) → CALLBACK HANDLER
 *    Verifies the install, bounces mobile back to the app via Universal Link / scheme.
 *
 * GitHub App "Setup URL" (github.com/settings/apps/chattylimkc):
 *    https://limkc.com/integrations/github        (production)
 *    http://localhost:3000/integrations/github    (dev)
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { authedFetch } from "../api/authedFetch";

const GITHUB_APP_INSTALL_URL_BASE = "https://github.com/apps/chattylimkc/installations/new";
const MEMORY_API = process.env.REACT_APP_API_UPLOAD_GATEWAY_URL
  ? process.env.REACT_APP_API_UPLOAD_GATEWAY_URL.replace(/\/ai\/?$/, "/memory")
  : "https://api.limkc.com/upload/memory";

// Universal Link attempted first; falls back to custom scheme after timeout
const IOS_UNIVERSAL_BASE = "https://limkc.com/integrations/github";
const MOBILE_SCHEME = "limkc://github-connected";

export default function IntegrationGithub() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const installationIdParam = searchParams.get("installation_id");
  const setupAction = searchParams.get("setup_action");
  const isCallback = Boolean(installationIdParam) && (setupAction === "install" || setupAction === "update");

  // accessToken now in HttpOnly cookie (invisible to JS). Use idToken as
  // logged-in sentinel — it stays in localStorage (out of cookie scope).
  const [authState, setAuthState] = useState({ loggedIn: Boolean(localStorage.getItem("idToken")) });
  const [status, setStatus] = useState({ loading: isCallback || authState.loggedIn, installed: false, installationId: null, error: null });
  const [callbackPhase, setCallbackPhase] = useState(isCallback ? "verifying" : null);

  const callMemoryApi = useCallback(async (action, body = {}) => {
    if (!localStorage.getItem("idToken")) throw new Error("Not logged in");
    const r = await authedFetch(MEMORY_API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action, ...body }),
    });
    if (!r.ok) throw new Error(`${action}: HTTP ${r.status}`);
    return r.json();
  }, []);

  const refreshStatus = useCallback(async () => {
    if (!authState.loggedIn) return;
    setStatus((s) => ({ ...s, loading: true }));
    try {
      const resp = await callMemoryApi("getGithubInstallation");
      setStatus({
        loading: false,
        installed: Boolean(resp.installation_id),
        installationId: resp.installation_id || null,
        error: null,
      });
    } catch (e) {
      setStatus({ loading: false, installed: false, installationId: null, error: e.message });
    }
  }, [callMemoryApi, authState.loggedIn]);

  // Callback: finish the install, bounce mobile back to app
  useEffect(() => {
    if (!isCallback) return;
    if (!authState.loggedIn) {
      // Defer: ask user to sign in first, we'll process on return
      sessionStorage.setItem("pendingGithubInstall", JSON.stringify({
        installation_id: installationIdParam,
        state: searchParams.get("state"),
      }));
      setCallbackPhase("needs_login");
      return;
    }
    (async () => {
      try {
        await callMemoryApi("setGithubInstallation", {
          installation_id: Number(installationIdParam),
          state: searchParams.get("state"),
        });
        window.history.replaceState({}, "", "/integrations/github");
        setCallbackPhase("complete");

        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        if (isMobile) {
          // Try Universal Link first (iOS will route to app if installed). Fall back to
          // custom scheme after a short timeout in case Universal Link isn't registered.
          const t = setTimeout(() => {
            window.location.href = `${MOBILE_SCHEME}?installation_id=${installationIdParam}`;
          }, 800);
          // If we're already inside the app via UL, the page unmounts before timeout fires
          return () => clearTimeout(t);
        }
        await refreshStatus();
      } catch (e) {
        setCallbackPhase("error");
        setStatus({ loading: false, installed: false, installationId: null, error: e.message });
      }
    })();
  }, [isCallback, installationIdParam, searchParams, callMemoryApi, authState.loggedIn, refreshStatus]);

  // Non-callback path: just refresh status if logged in
  useEffect(() => {
    if (isCallback) return;
    if (authState.loggedIn) refreshStatus();
  }, [isCallback, authState.loggedIn, refreshStatus]);

  const handleConnect = async () => {
    try {
      const resp = await callMemoryApi("getGithubInstallState");
      const url = `${GITHUB_APP_INSTALL_URL_BASE}?state=${encodeURIComponent(resp.state)}`;
      window.location.href = url;
    } catch (e) {
      setStatus((s) => ({ ...s, error: `Could not start install: ${e.message}` }));
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect your GitHub App? The AI will lose repo access. You can reconnect anytime.")) return;
    try {
      await callMemoryApi("deleteGithubInstallation");
      await refreshStatus();
    } catch (e) {
      alert(`Disconnect failed: ${e.message}`);
    }
  };

  // ----- Rendering -------------------------------------------------------

  // Case A: mid-callback
  if (isCallback) {
    return (
      <>
        <Helmet>
          <title>Connecting GitHub… · Chatty by limkc</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div style={styles.page}>
          <div style={styles.card}>
            <h1 style={styles.h1}>Connecting GitHub…</h1>
            {callbackPhase === "verifying" && <p style={styles.sub}>One moment — verifying your installation.</p>}
            {callbackPhase === "complete" && (
              <>
                <div style={styles.connected}>✓ Connected. You can close this tab or return to the Chatty app.</div>
                <div style={styles.buttonRow}>
                  <button style={styles.btnPrimary} onClick={() => navigate("/chatty")}>Open Chatty</button>
                </div>
              </>
            )}
            {callbackPhase === "needs_login" && (
              <>
                <p style={styles.sub}>Sign in to finish connecting your GitHub installation.</p>
                <div style={styles.buttonRow}>
                  <button style={styles.btnPrimary} onClick={() => navigate(`/login?redirect=${encodeURIComponent("/integrations/github")}`)}>Sign in</button>
                </div>
              </>
            )}
            {callbackPhase === "error" && (
              <div style={styles.error}>Could not complete install: {status.error}</div>
            )}
          </div>
        </div>
      </>
    );
  }

  // Case B: logged-in management view
  if (authState.loggedIn) {
    return (
      <>
        <Helmet>
          <title>GitHub integration · Chatty by limkc</title>
          <meta name="description" content="Connect your GitHub App to let Chatty AI work with your repos." />
        </Helmet>
        <div style={styles.page}>
          <div style={styles.card}>
            <h1 style={styles.h1}>GitHub integration</h1>
            <p style={styles.sub}>
              Connect the <strong>chattylimkc</strong> GitHub App so the AI can list your repos,
              read files, and open pull requests on your behalf.
            </p>
            {status.loading && <div style={styles.muted}>Loading status…</div>}
            {!status.loading && status.error && <div style={styles.error}>Error: {status.error}</div>}
            {!status.loading && !status.error && status.installed && (
              <>
                <div style={styles.connected}>✓ Connected (installation {status.installationId})</div>
                <p style={styles.sub}>
                  To change which repos the AI can see, visit{" "}
                  <a href={`https://github.com/settings/installations/${status.installationId}`} target="_blank" rel="noreferrer" style={styles.link}>
                    your GitHub installation settings
                  </a>.
                </p>
                <div style={styles.buttonRow}>
                  <button style={styles.btnSecondary} onClick={handleDisconnect}>Disconnect</button>
                  <button style={styles.btnPrimary} onClick={() => navigate("/account")}>Back to account</button>
                </div>
              </>
            )}
            {!status.loading && !status.error && !status.installed && (
              <>
                <ul style={styles.steps}>
                  <li>Click <strong>Connect GitHub</strong> — you'll be taken to GitHub.</li>
                  <li>Choose which repos to grant access to (all, or a specific list).</li>
                  <li>GitHub redirects back here and the connection is saved automatically.</li>
                  <li>Enable the <em>GitHub</em> tool in Settings → Tool preferences.</li>
                </ul>
                <div style={styles.safetyBox}>
                  <strong>Safety guardrails:</strong>
                  <ul style={styles.safetyList}>
                    <li>The AI never commits to <code>main</code> or <code>master</code>.</li>
                    <li>Every change goes through a pull request.</li>
                    <li>No force-push, no branch deletion, no repo deletion.</li>
                  </ul>
                </div>
                <div style={styles.buttonRow}>
                  <button style={styles.btnPrimary} onClick={handleConnect}>Connect GitHub</button>
                </div>
              </>
            )}
          </div>
        </div>
      </>
    );
  }

  // Case C: marketing page — not logged in, no query params
  return (
    <>
      <Helmet>
        <title>GitHub integration — work with your repos in Chatty | limkc</title>
        <meta name="description" content="Chatty by limkc connects to your GitHub repos. Let AI list files, explain code, and open pull requests — safely, via the chattylimkc GitHub App. Never commits to main, always reviews via PR." />
        <meta property="og:title" content="GitHub integration — Chatty AI" />
        <meta property="og:description" content="Let AI work with your repos. List files, explain code, open pull requests — safely via GitHub App." />
        <meta property="og:url" content="https://limkc.com/integrations/github" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://limkc.com/integrations/github" />
      </Helmet>
      <div style={styles.marketingPage}>
        <main style={styles.marketingMain}>
          <section style={styles.hero}>
            <div style={styles.heroKicker}>Integration · GitHub</div>
            <h1 style={styles.heroTitle}>Work with your repos, from inside the chat.</h1>
            <p style={styles.heroSub}>
              Chatty connects to GitHub through the <strong>chattylimkc</strong> App. Once installed,
              the AI can list your repositories, read specific files, and open pull requests on
              your behalf — with guardrails that never let it touch <code>main</code> directly.
            </p>
            <div style={styles.ctaRow}>
              <a href="/register" style={styles.btnPrimary}>Get started — free</a>
              <a href="https://apps.apple.com/app/chatty" target="_blank" rel="noreferrer" style={styles.btnSecondary}>
                Download for iOS
              </a>
            </div>
          </section>

          <section style={styles.features}>
            <h2 style={styles.h2}>What the AI can do with your repos</h2>
            <div style={styles.featureGrid}>
              <FeatureCard title="List repos" body="Ask “which repos do I have?” and the AI replies with names, descriptions, languages, and default branches." />
              <FeatureCard title="Read files" body="“What does src/auth.py do?” — the AI fetches the file content (up to 100KB) and explains it." />
              <FeatureCard title="Open pull requests" body="Approve a proposed change and the AI creates a feature branch, commits files, opens a PR, and links it back." />
              <FeatureCard title="Scope per session" body="Bind a chat to a specific repo + branch so the AI stays focused. Switch repos by unbinding or starting a new chat." />
            </div>
          </section>

          <section style={styles.safety}>
            <h2 style={styles.h2}>Safety by default</h2>
            <ul style={styles.safetyList}>
              <li>Never commits to <code>main</code>, <code>master</code>, <code>develop</code>, or <code>production</code>.</li>
              <li>Every code change goes through a pull request so you review before merging.</li>
              <li>No force-push. No branch deletion. No repo deletion.</li>
              <li>You choose which repos the app can see — grant all, or just a specific list.</li>
              <li>Disconnect anytime from <a href="https://github.com/settings/installations" target="_blank" rel="noreferrer" style={styles.link}>GitHub settings</a>.</li>
            </ul>
          </section>

          <section style={styles.cta}>
            <h2 style={styles.h2}>Ready to try it?</h2>
            <p style={styles.sub}>Sign in or create a free account, then head to <a href="/integrations/github" style={styles.link}>Integrations → GitHub</a> to connect.</p>
            <div style={styles.ctaRow}>
              <a href={`/login?redirect=${encodeURIComponent("/integrations/github")}`} style={styles.btnPrimary}>Sign in</a>
              <a href="/register" style={styles.btnSecondary}>Create an account</a>
            </div>
          </section>

          <footer style={styles.footer}>
            <a href="/" style={styles.link}>Home</a>
            <span style={styles.footerDot}>·</span>
            <a href="/chatty" style={styles.link}>Try Chatty</a>
            <span style={styles.footerDot}>·</span>
            <a href="/privacy" style={styles.link}>Privacy</a>
            <span style={styles.footerDot}>·</span>
            <a href="/terms" style={styles.link}>Terms</a>
          </footer>
        </main>
      </div>
    </>
  );
}

function FeatureCard({ title, body }) {
  return (
    <div style={styles.featureCard}>
      <h3 style={styles.featureTitle}>{title}</h3>
      <p style={styles.featureBody}>{body}</p>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    padding: "80px 24px 40px",
    background: "var(--bg-primary, #0a0a0b)",
  },
  card: {
    maxWidth: 640, width: "100%",
    background: "var(--bg-card, #111113)",
    border: "1px solid var(--border-primary, #222227)",
    borderRadius: 12, padding: 32,
    color: "var(--text-primary, #e8e8ea)",
  },
  h1: { fontSize: 22, fontWeight: 600, margin: "0 0 8px", letterSpacing: "-0.01em" },
  h2: { fontSize: 20, fontWeight: 600, margin: "32px 0 12px", letterSpacing: "-0.01em", color: "var(--text-primary, #e8e8ea)" },
  sub: { color: "var(--text-secondary, #8a8a92)", fontSize: 14, lineHeight: 1.55, margin: "8px 0 20px" },
  muted: { color: "var(--text-secondary, #8a8a92)", fontSize: 13 },
  error: { background: "#2a1515", border: "1px solid #501a1a", color: "#ff9090", padding: 12, borderRadius: 8, fontSize: 13 },
  connected: { background: "#0f2018", border: "1px solid #1a4030", color: "#82e7a7", padding: 12, borderRadius: 8, fontSize: 14, margin: "16px 0" },
  steps: { paddingLeft: 20, color: "var(--text-primary, #e8e8ea)", fontSize: 14, lineHeight: 1.8, margin: "12px 0 20px" },
  safetyBox: { background: "#15151a", border: "1px solid #1f1f25", borderRadius: 8, padding: "12px 16px", fontSize: 13, color: "var(--text-secondary, #a0a0a8)", margin: "16px 0" },
  safetyList: { margin: "8px 0 0", paddingLeft: 18, lineHeight: 1.7, color: "var(--text-secondary, #a0a0a8)", fontSize: 14 },
  buttonRow: { display: "flex", gap: 12, marginTop: 20, flexWrap: "wrap" },
  btnPrimary: { padding: "10px 20px", background: "#3b6ef5", color: "white", border: 0, borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: "pointer", textDecoration: "none", display: "inline-block" },
  btnSecondary: { padding: "10px 20px", background: "transparent", color: "var(--text-primary, #e8e8ea)", border: "1px solid var(--border-primary, #222227)", borderRadius: 8, fontSize: 14, cursor: "pointer", textDecoration: "none", display: "inline-block" },
  link: { color: "#3b6ef5", textDecoration: "none" },

  // Marketing-specific
  marketingPage: { minHeight: "100vh", background: "var(--bg-primary, #0a0a0b)", color: "var(--text-primary, #e8e8ea)" },
  marketingMain: { maxWidth: 960, margin: "0 auto", padding: "80px 24px 60px" },
  hero: { textAlign: "left" },
  heroKicker: { fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text-secondary, #8a8a92)", marginBottom: 12 },
  heroTitle: { fontSize: 44, fontWeight: 700, margin: "0 0 16px", lineHeight: 1.15, letterSpacing: "-0.02em" },
  heroSub: { fontSize: 16, lineHeight: 1.65, maxWidth: 720, color: "var(--text-secondary, #b0b0b8)", marginBottom: 24 },
  ctaRow: { display: "flex", gap: 12, marginTop: 16, flexWrap: "wrap" },
  features: { marginTop: 56 },
  featureGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16, marginTop: 16 },
  featureCard: { background: "var(--bg-card, #111113)", border: "1px solid var(--border-primary, #222227)", borderRadius: 12, padding: 20 },
  featureTitle: { fontSize: 16, fontWeight: 600, margin: "0 0 8px" },
  featureBody: { fontSize: 14, lineHeight: 1.55, color: "var(--text-secondary, #a0a0a8)", margin: 0 },
  safety: { marginTop: 40 },
  cta: { marginTop: 56 },
  footer: { marginTop: 72, paddingTop: 24, borderTop: "1px solid var(--border-primary, #222227)", fontSize: 13, color: "var(--text-secondary, #8a8a92)", display: "flex", gap: 8, flexWrap: "wrap" },
  footerDot: { color: "var(--text-secondary, #555)" },
};
